import { Button, toast } from "@medusajs/ui"
import { Fragment, useState, type ReactElement } from "react"
import { Link } from "react-router-dom"
import { useCan } from "@/lib/permissions"
import { useSyncAdSpend, type AdCampaigns, type AdRow } from "./api"
import { count, dayLabel, money, timeOfDay } from "./format"
import { Card, MoreLink, Muted } from "./parts"

/**
 * "Ad campaigns" (Home, and Analytics → Reports → Ad campaigns): where the sales come from, per
 * campaign of the ad account. Three groups of columns, each saying whose numbers they are: the
 * platform's spend, Meta's own purchases (its attribution), and Keel's last-click orders. Campaigns
 * expand to ad sets and ads. Data: backend lib/home/ad-campaigns.ts.
 */
const ratio = (v: number | null | undefined) => (v == null ? "—" : `${v.toFixed(2)}×`)
const dash = <span className="text-[var(--k-ink-muted)]">—</span>

function syncedLabel(iso: string | null, tz: string) {
  if (!iso) return "never"
  const day = new Intl.DateTimeFormat("en-CA", { timeZone: tz }).format(new Date(iso))
  const today = new Intl.DateTimeFormat("en-CA", { timeZone: tz }).format(new Date())
  return day === today ? timeOfDay(iso, tz) : `${dayLabel(day)}, ${timeOfDay(iso, tz)}`
}

export function AdCampaignsPanel({ ads, tz, showAnalyticsLink = true }: { ads: AdCampaigns; tz: string; showAnalyticsLink?: boolean }) {
  const can = useCan()
  const sync = useSyncAdSpend()
  const [open, setOpen] = useState<Set<string>>(new Set())
  const connected = (["meta", "google"] as const).filter((p) => ads.platforms[p].connected)
  const last = connected.map((p) => ads.platforms[p]).sort((a, b) => (b.last_sync_at ?? "").localeCompare(a.last_sync_at ?? ""))[0]
  const failed = connected.filter((p) => ads.platforms[p].last_sync_ok === false)
  const toggle = (k: string) => setOpen((s) => (s.has(k) ? (s.delete(k), new Set(s)) : new Set(s.add(k))))
  const runSync = () =>
    sync.mutate(connected, {
      onSuccess: (r) => {
        const bad = r.filter((x) => !x.ok)
        if (bad.length) toast.error(bad.map((x) => x.message).join(" "))
        else toast.success(`Spend synced (${r.reduce((s, x) => s + (x.rows ?? 0), 0)} ad × day rows)`)
      },
    })
  const t = ads.total
  const o = ads.orders

  const cells = (r: Pick<AdRow, "spend" | "impressions" | "clicks" | "cpc" | "meta_purchases" | "meta_purchase_value" | "meta_roas" | "orders" | "revenue" | "roas">) => (
    <>
      <td className="k-data px-3 py-2.5 text-right">{money(r.spend)}</td>
      <td className="k-data px-3 py-2.5 text-right">{count(r.impressions)}</td>
      <td className="k-data px-3 py-2.5 text-right">{count(r.clicks)}</td>
      <td className="k-data px-3 py-2.5 text-right">{r.cpc == null ? dash : money(r.cpc)}</td>
      <td className="k-data border-l border-[var(--k-line)] px-3 py-2.5 text-right">{r.meta_purchases == null ? dash : count(r.meta_purchases)}</td>
      <td className="k-data px-3 py-2.5 text-right">{r.meta_purchase_value == null ? dash : money(r.meta_purchase_value)}</td>
      <td className="k-data px-3 py-2.5 text-right">{r.meta_roas == null ? dash : ratio(r.meta_roas)}</td>
      <td className="k-data border-l border-[var(--k-line)] px-3 py-2.5 text-right">{count(r.orders)}</td>
      <td className="k-data px-3 py-2.5 text-right">{money(r.revenue)}</td>
      <td className="k-data px-3 py-2.5 pr-4 text-right">{r.roas == null ? dash : ratio(r.roas)}</td>
    </>
  )
  const row = (r: AdRow, depth: number): ReactElement => {
    const expandable = r.children.length > 0 && r.level !== "ad"
    const isOpen = open.has(r.key)
    return (
      <Fragment key={r.key}>
        <tr className={depth ? "bg-[var(--k-sunken)]" : ""} data-testid={`ad-row-${r.level}`}>
          <td className="max-w-[320px] px-3 py-2.5 pl-4">
            <div className="flex min-w-0 items-center gap-1.5" style={{ paddingLeft: depth * 16 }}>
              {expandable ? (
                <button type="button" aria-expanded={isOpen} aria-label={`${isOpen ? "Hide" : "Show"} ${r.level === "campaign" ? "ad sets" : "ads"} of ${r.name}`} onClick={() => toggle(r.key)} className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-[var(--k-ink-muted)] hover:bg-[var(--k-blue-50)]">
                  <span aria-hidden className={`inline-block transition-transform ${isOpen ? "rotate-90" : ""}`}>›</span>
                </button>
              ) : (
                <span className="w-5 shrink-0" />
              )}
              <span className="min-w-0 truncate text-[var(--k-ink)]" title={r.id ? `${r.name} (ID ${r.id})` : r.name}>
                {r.name}
              </span>
              {depth === 0 && <span className="shrink-0 text-[12px] text-[var(--k-ink-muted)]">{r.platform_label}</span>}
              {r.matched_on_name > 0 && (
                <span className="shrink-0 cursor-help text-[12px] text-[var(--k-ink-muted)] underline decoration-dotted" title={`${r.matched_on_name} order${r.matched_on_name === 1 ? "" : "s"} tied to this ${r.level === "adset" ? "ad set" : r.level} by its name only (the link had no ID). Two campaigns with the same name would share them.`}>
                  by name
                </span>
              )}
            </div>
          </td>
          {cells(r)}
        </tr>
        {expandable && isOpen && r.children.map((c) => row(c, depth + 1))}
      </Fragment>
    )
  }

  return (
    <Card
      title="Ad campaigns"
      right={
        <div className="flex flex-wrap items-center gap-3">
          {connected.length > 0 && (
            <span className={`text-[13px] ${failed.length ? "text-[var(--k-alert)]" : "text-[var(--k-ink-muted)]"}`} title={failed.map((p) => ads.platforms[p].last_sync_error).filter(Boolean).join(" ") || "Spend syncs every hour (yesterday and today), and the last 3 days once a day."} data-testid="ad-last-synced">
              {failed.length ? "Last sync failed · " : ""}Last synced {syncedLabel(last?.last_sync_at ?? null, tz)}
            </span>
          )}
          {connected.length > 0 && can("analytics:write") && (
            <Button size="small" variant="secondary" isLoading={sync.isPending} onClick={runSync} data-testid="ad-sync-now">
              Sync now
            </Button>
          )}
          {showAnalyticsLink && <MoreLink to="/analytics/reports/ad_campaigns">Open in Analytics</MoreLink>}
        </div>
      }
      footer={
        <>
          Spend, impressions and clicks are what the ad platforms report. <strong className="font-medium text-[var(--k-ink)]">As reported by Meta</strong>: Meta's own purchases from the pixel and the Conversions API, counted with Meta's attribution window (by default 7 days after a click or 1 day after a view, including modelled conversions), as Ads Manager shows them. <strong className="font-medium text-[var(--k-ink)]">Keel</strong>: orders whose last paid click in the {ads.window_days} days before the order was this campaign, product revenue net of discounts and refunds (no shipping or tax). The two count differently and can differ.
        </>
      }
    >
      <div data-testid="ad-campaigns">
        {ads.notice.show && <TaggingNotice ads={ads} />}
        {o.all > 0 && (
          <Muted className="mb-2">
            <span data-testid="ad-orders-credited">
              Keel credited {count(o.attributed)} of {count(o.all)} order{o.all === 1 ? "" : "s"} in this period to an ad
            </span>
            {o.organic_social > 0 ? ` · ${count(o.organic_social)} from Facebook / Instagram clicks without campaign parameters (Organic social)` : ""}
            {o.unattributed > 0 ? ` · ${count(o.unattributed)} with no ad click` : ""}.
          </Muted>
        )}
        {ads.rows.length === 0 ? (
          <Muted className="py-6 text-center">
            {connected.length === 0 ? (
              <>
                No ad account connected. Connect Meta in <Link className="text-[var(--k-blue)] hover:underline" to="/settings/customer-events">Settings → Customer events → Ad spend</Link>.
              </>
            ) : (
              "No ad spend or ad-credited orders in this period."
            )}
          </Muted>
        ) : (
          <div className="-mx-4 overflow-x-auto sm:-mx-5">
            <table className="w-full min-w-[980px] text-[14px]" data-testid="ad-campaigns-table">
              <thead>
                <tr className="text-[12px] text-[var(--k-ink-muted)]">
                  <th scope="col" />
                  <th scope="colgroup" colSpan={4} className="px-3 pt-1 text-right font-normal">
                    Ad platform
                  </th>
                  <th scope="colgroup" colSpan={3} className="border-l border-[var(--k-line)] px-3 pt-1 text-right font-normal">
                    As reported by Meta
                  </th>
                  <th scope="colgroup" colSpan={3} className="border-l border-[var(--k-line)] px-3 pt-1 pr-4 text-right font-normal">
                    Keel · last click, {ads.window_days} days
                  </th>
                </tr>
                <tr className="border-y border-[var(--k-line)]">
                  {["Campaign", "Spend", "Impr.", "Clicks", "CPC", "Purchases", "Value", "ROAS", "Orders", "Revenue", "ROAS"].map((h, i) => (
                    <th key={i} scope="col" className={`k-eyebrow px-3 py-2 font-medium ${i === 0 ? "pl-4 text-left" : "text-right"} ${i === 5 || i === 8 ? "border-l border-[var(--k-line)]" : ""} ${i === 10 ? "pr-4" : ""}`}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--k-line)]">{ads.rows.map((r) => row(r, 0))}</tbody>
              <tfoot>
                <tr className="border-t border-[var(--k-line)] font-medium" data-testid="ad-total">
                  <td className="px-3 py-2.5 pl-4">Total</td>
                  {cells(t)}
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </Card>
  )
}

/** The honest gap: Facebook / Instagram clicks without campaign parameters can't be credited to a campaign. */
function TaggingNotice({ ads }: { ads: AdCampaigns }) {
  const v = ads.meta_visits
  const copy = () => navigator.clipboard.writeText(ads.notice.url_parameters).then(() => toast.success("URL parameters copied"), () => toast.error("Couldn't copy: select the text and copy it"))
  return (
    <div className="mb-3 rounded-[var(--k-r-sm)] bg-[var(--k-brass-50)] px-3.5 py-3 text-[13px] leading-5 text-[var(--k-ink)]" role="note" data-testid="ad-tagging-notice">
      <p>
        {ads.notice.reason === "fbclid_only" ? (
          <>
            <strong className="font-medium">{count(v.fbclid_only)} of {count(v.total)} Facebook / Instagram visits</strong> in this period came with only Meta's click ID (fbclid), which doesn't say which campaign they came from, so Keel can't credit those sales to a campaign (they show as Organic social).
          </>
        ) : (
          <>Meta spent money in this period but no visit arrived with campaign parameters, so Keel can't credit any sale to a campaign.</>
        )}{" "}
        Meta's own purchase figures above don't depend on this. To see campaigns in Keel too, paste this into Meta Ads Manager on every ad (Ad level → Tracking → URL parameters):
      </p>
      <div className="mt-2 flex items-start gap-2">
        <code className="min-w-0 flex-1 select-all break-all rounded-[var(--k-r-sm)] bg-[var(--k-surface)] px-2.5 py-1.5 font-[var(--k-font-mono)] text-[12px]" data-testid="ad-url-parameters">
          {ads.notice.url_parameters}
        </code>
        <Button size="small" variant="secondary" type="button" onClick={copy}>
          Copy
        </Button>
      </div>
      <p className="mt-2 text-[var(--k-ink-muted)]">Sales from ads clicked before the change can't be credited afterwards.</p>
    </div>
  )
}
